import { apiClient } from '@/lib/api-client';
import type {
  DID,
  DIDLocator,
  DIDMetrics,
  UpdateCNAMRequest,
  UpdateForwardRequest,
  UpdateFailoverRequest,
  UpdateConferenceRequest,
  UpdateFaxToEmailRequest,
  ForwardUpdateResponse,
  FailoverUpdateResponse,
  ConferenceUpdateResponse,
  FaxToEmailUpdateResponse,
  Update911AddressRequest,
  UpdateCallerIDRequest,
  E911Address,
  EndpointGroup,
  DIDGroup,
  PortOrder,
  PortabilityCheck,
  PortOrderSubmitRequest,
  PortOrderSubmitResponse,
  PortOrderWithSignatureRequest,
  PortOrderWithSignatureResponse,
  TelviaIQOrder,
  CDRListResponse,
  CDRTrendItem,
} from '../types';

const BASE_URL = '/api/v1/dids';

/**
 * Fetch all DIDs for the current account
 */
export async function getDIDs(): Promise<DID[]> {
  const response = await apiClient.get(`${BASE_URL}/`);
  return response.data.data || response.data;
}

/**
 * Fetch a single DID by number
 */
export async function getDID(tn: string): Promise<DID> {
  const response = await apiClient.get(`${BASE_URL}/${tn}/`);
  return response.data.data || response.data;
}

/**
 * Fetch DID dashboard metrics
 */
export async function getDIDMetrics(): Promise<DIDMetrics> {
  const response = await apiClient.get(`${BASE_URL}/dashboard/`);
  return response.data.data || response.data;
}

/**
 * Update CNAM (Caller Name) setting
 */
export async function updateCNAM(data: UpdateCNAMRequest): Promise<DID> {
  const response = await apiClient.put(`${BASE_URL}/${data.tn}/cnam/update/`, {
    enabled: data.enabled,
  });
  return response.data.data || response.data;
}

/**
 * Update call forwarding
 */
export async function updateForward(data: UpdateForwardRequest): Promise<ForwardUpdateResponse> {
  const response = await apiClient.put(`${BASE_URL}/${data.tn}/forward/update/`, {
    enabled: data.enabled,
    forward_number: data.forwardNumber,
  });
  return response.data.data || response.data;
}

/**
 * Update failover number
 */
export async function updateFailover(data: UpdateFailoverRequest): Promise<FailoverUpdateResponse> {
  const response = await apiClient.put(`${BASE_URL}/${data.tn}/failover/update/`, {
    enabled: data.enabled,
    failover_number: data.failoverNumber,
  });
  return response.data.data || response.data;
}

/**
 * Update conference line
 *
 * CRITICAL: Disabling conference removes ALL routing config from the DID.
 * The backend automatically restores the original endpoint group.
 */
export async function updateConference(data: UpdateConferenceRequest): Promise<ConferenceUpdateResponse> {
  const response = await apiClient.put(`${BASE_URL}/${data.tn}/conference/update/`, {
    enabled: data.enabled,
    pin: data.pin,
  });
  return response.data.data || response.data;
}

/**
 * Update fax-to-email
 */
export async function updateFaxToEmail(data: UpdateFaxToEmailRequest): Promise<FaxToEmailUpdateResponse> {
  const response = await apiClient.put(`${BASE_URL}/${data.tn}/fax-to-email/update/`, {
    enabled: data.enabled,
    email: data.email,
  });
  return response.data.data || response.data;
}

/**
 * Get 911 address for a DID
 */
export async function get911Address(tn: string): Promise<E911Address | null> {
  const response = await apiClient.get(`${BASE_URL}/${tn}/e911/`);
  return response.data.data || response.data;
}

/**
 * Update 911 address
 */
export async function update911Address(data: Update911AddressRequest): Promise<E911Address> {
  const response = await apiClient.put(`${BASE_URL}/${data.tn}/e911/update/`, {
    address1: data.address1,
    address2: data.address2,
    city: data.city,
    state: data.state,
    zip_code: data.zipCode,
    plus_four: data.plusFour,
    caller_name: data.callerName,
  });
  return response.data.data || response.data;
}

/**
 * Remove 911 address
 */
export async function remove911Address(tn: string): Promise<void> {
  await apiClient.delete(`${BASE_URL}/${tn}/e911/`);
}

/**
 * Get 911 alerts for a DID
 */
export async function get911Alerts(tn: string): Promise<string[]> {
  const response = await apiClient.get(`${BASE_URL}/${tn}/e911/alerts/`);
  const data = response.data.data || response.data;
  return data.alerts || [];
}

/**
 * Add 911 alert email
 */
export async function add911Alert(tn: string, email: string): Promise<void> {
  await apiClient.post(`${BASE_URL}/${tn}/e911/alerts/add/`, { email });
}

/**
 * Remove 911 alert email
 */
export async function remove911Alert(tn: string, email: string): Promise<void> {
  await apiClient.post(`${BASE_URL}/${tn}/e911/alerts/remove/`, { email });
}

/**
 * Update Caller ID / CNAM Storage display name
 */
export async function updateCallerID(data: UpdateCallerIDRequest): Promise<DID> {
  const response = await apiClient.put(`${BASE_URL}/${data.tn}/caller-id/update/`, {
    enabled: data.enabled,
    display_name: data.displayName,
  });
  return response.data.data || response.data;
}

/**
 * Get available endpoint groups
 */
export async function getEndpointGroups(): Promise<EndpointGroup[]> {
  const response = await apiClient.get('/api/v1/endpoint-groups/');
  return response.data.data || response.data;
}

/**
 * Release (delete) a DID
 */
export async function releaseDID(tn: string): Promise<void> {
  await apiClient.delete(`${BASE_URL}/${tn}/`);
}

// =============================================================================
// VI Inventory APIs (DID Ordering)
// =============================================================================

/**
 * Get list of states with available DID inventory
 */
export async function getAvailableStates(): Promise<string[]> {
  const response = await apiClient.get(`${BASE_URL}/vi/inventory/states/`);
  return response.data.data?.states || [];
}

/**
 * Get NPAs (area codes) available in a state
 */
export async function getAvailableNPAs(state: string): Promise<string[]> {
  const response = await apiClient.get(`${BASE_URL}/vi/inventory/npas/`, {
    params: { state },
  });
  return response.data.data?.npas || [];
}

/**
 * Get rate centers available in a state
 */
export async function getAvailableRateCenters(state: string): Promise<string[]> {
  const response = await apiClient.get(`${BASE_URL}/vi/inventory/rate-centers/`, {
    params: { state },
  });
  return response.data.data?.rate_centers || [];
}

/**
 * Search available DIDs in inventory
 */
export interface SearchInventoryParams {
  state?: string;
  npa?: string;
  nxx?: string;
  rate_center?: string;
  tier?: string;
  t38?: boolean;
  cnam?: boolean;
  sms?: boolean;
  limit?: number;
}

export interface SearchInventoryResult {
  dids: DIDLocator[];
  total: number;
  returned: number;
}

export async function searchInventory(params: SearchInventoryParams): Promise<SearchInventoryResult> {
  const response = await apiClient.get(`${BASE_URL}/vi/inventory/search/`, { params });
  return response.data.data || { dids: [], total: 0, returned: 0 };
}

/**
 * Get endpoint groups available to user for ordering
 */
export async function getOrderEndpointGroups(): Promise<EndpointGroup[]> {
  const response = await apiClient.get(`${BASE_URL}/vi/inventory/endpoint-groups/`);
  return response.data.data || [];
}

/**
 * Get DID groups available to user for ordering
 */
export async function getOrderDIDGroups(): Promise<DIDGroup[]> {
  const response = await apiClient.get(`${BASE_URL}/vi/inventory/did-groups/`);
  return response.data.data || [];
}

/**
 * Order a single DID
 */
export interface OrderDIDRequest {
  tn: string;
  endpoint_group_id: number;
  did_group_id: number;
  ref_id?: string;
  cnam?: boolean;
}

export interface OrderDIDResult {
  success: boolean;
  did: DID | null;
  message: string;
}

export async function orderDID(order: OrderDIDRequest): Promise<OrderDIDResult> {
  const response = await apiClient.post(`${BASE_URL}/vi/inventory/order/`, order);
  return response.data.data || response.data;
}

/**
 * Order multiple DIDs
 */
export interface OrderDIDsBulkResult {
  successful: Array<{ tn: string; endpoint_group_id: number; did_group_id: number; result: OrderDIDResult }>;
  failed: Array<{ tn: string; endpoint_group_id: number; did_group_id: number; error: string }>;
  total: number;
  success_count: number;
  fail_count: number;
}

export async function orderDIDsBulk(orders: OrderDIDRequest[]): Promise<OrderDIDsBulkResult> {
  // Use extended timeout for bulk orders (2 API calls per DID can take time)
  // ~5 seconds per DID * number of DIDs, minimum 60 seconds
  const timeoutMs = Math.max(60000, orders.length * 5000);
  const response = await apiClient.post(`${BASE_URL}/vi/inventory/order/bulk/`, { orders }, {
    timeout: timeoutMs,
  });
  return response.data.data || response.data;
}

// =============================================================================
// Port Order APIs (Number Porting)
// =============================================================================

/**
 * Get list of active port orders
 */
export async function getPortOrders(): Promise<PortOrder[]> {
  const response = await apiClient.get(`${BASE_URL}/vi/ports/`);
  return response.data.data || [];
}

/**
 * Get details for a specific port order
 */
export async function getPortOrder(portId: number): Promise<PortOrder> {
  const response = await apiClient.get(`${BASE_URL}/vi/ports/${portId}/`);
  return response.data.data || response.data;
}

/**
 * Check if a phone number is portable
 */
export async function checkPortability(tn: string): Promise<PortabilityCheck> {
  const response = await apiClient.post(`${BASE_URL}/vi/ports/check/`, { tn });
  return response.data.data || response.data;
}

/**
 * Submit a port order request via email
 */
export async function submitPortOrder(data: PortOrderSubmitRequest): Promise<PortOrderSubmitResponse> {
  const response = await apiClient.post(`${BASE_URL}/vi/ports/submit/`, data);
  return response.data.data || response.data;
}

/**
 * Submit a port order request with e-signature
 */
export async function submitPortOrderWithSignature(
  data: PortOrderWithSignatureRequest
): Promise<PortOrderWithSignatureResponse> {
  const response = await apiClient.post(`${BASE_URL}/vi/ports/submit-with-signature/`, data);
  return response.data.data || response.data;
}

/**
 * Filter options for TelviaIQ orders
 */
export interface TelviaIQOrderFilters {
  orderType?: string;
  status?: string;
  tn?: string;
  user?: string;
  limit?: number;
}

/**
 * Get TelviaIQ orders (activity log)
 */
export async function getTelviaIQOrders(filters: TelviaIQOrderFilters = {}): Promise<TelviaIQOrder[]> {
  const params: Record<string, string | number> = { limit: filters.limit || 500 };
  if (filters.orderType) params.order_type = filters.orderType;
  if (filters.status) params.status = filters.status;
  if (filters.tn) params.tn = filters.tn;
  if (filters.user) params.user = filters.user;
  const response = await apiClient.get(`${BASE_URL}/orders/internal/`, { params });
  return response.data.data || [];
}

// =============================================================================
// CDR APIs (Call Detail Records)
// =============================================================================

/**
 * Get MTD CDR usage summary
 */
export interface CDRMTDSummary {
  orig_local_minutes_mtd: number;
  orig_local_calls_mtd: number;
  orig_local_cost_mtd: number;
  orig_tf_minutes_mtd: number;
  orig_tf_calls_mtd: number;
  orig_tf_cost_mtd: number;
  orig_minutes_mtd: number;
  orig_calls_mtd: number;
  orig_cost_mtd: number;
  term_minutes_mtd: number;
  term_calls_mtd: number;
  term_cost_mtd: number;
  total_minutes_mtd: number;
  total_calls_mtd: number;
  total_cost_mtd: number;
}

export async function getCDRMTDSummary(year?: number, month?: number): Promise<CDRMTDSummary> {
  const params: Record<string, number> = {};
  if (year) params.year = year;
  if (month) params.month = month;
  const response = await apiClient.get(`${BASE_URL}/cdrs/mtd/`, { params });
  return response.data.data || response.data;
}

/**
 * Get monthly CDR trend data
 */
export async function getCDRTrend(months = 12): Promise<CDRTrendItem[]> {
  const response = await apiClient.get(`${BASE_URL}/cdrs/trend/`, {
    params: { months },
  });
  return response.data.data || [];
}

/**
 * Get paginated CDR list with filters
 */
export interface CDRListParams {
  start_date?: string;
  end_date?: string;
  call_type?: string;
  dnis?: string;
  ani?: string;
  endpoint_group?: string;
  search?: string;
  page?: number;
  page_size?: number;
}

export async function getCDRList(params: CDRListParams): Promise<CDRListResponse> {
  const response = await apiClient.get(`${BASE_URL}/cdrs/`, { params });
  return response.data.data || { records: [], total: 0, page: 1, pages: 0, page_size: 50 };
}

/**
 * Get per-DID CDR summary for the month
 */
export interface PerDIDSummary {
  dnis: string;
  minutes: number;
  calls: number;
  cost: number;
}

export async function getCDRPerDID(year?: number, month?: number, limit = 20): Promise<PerDIDSummary[]> {
  const params: Record<string, number> = { limit };
  if (year) params.year = year;
  if (month) params.month = month;
  const response = await apiClient.get(`${BASE_URL}/cdrs/per-did/`, { params });
  return response.data.data || [];
}

/**
 * Top DIDs by inbound (ORIG) calls - grouped by DNIS (customer's DID)
 */
export interface TopDIDSummary {
  did: string;
  minutes: number;
  calls: number;
  cost: number;
}

export async function getCDRTopInbound(year?: number, month?: number, limit = 10): Promise<TopDIDSummary[]> {
  const params: Record<string, number> = { limit };
  if (year) params.year = year;
  if (month) params.month = month;
  const response = await apiClient.get(`${BASE_URL}/cdrs/top-inbound/`, { params });
  return response.data.data || [];
}

/**
 * Top DIDs by outbound (TERM) calls - grouped by ANI (customer's DID)
 */
export async function getCDRTopOutbound(year?: number, month?: number, limit = 10): Promise<TopDIDSummary[]> {
  const params: Record<string, number> = { limit };
  if (year) params.year = year;
  if (month) params.month = month;
  const response = await apiClient.get(`${BASE_URL}/cdrs/top-outbound/`, { params });
  return response.data.data || [];
}

/**
 * Daily CDR trend data (for Daily chart view)
 */
export interface CDRDailyTrendItem {
  date: string;
  day_name: string;
  orig_minutes: number;
  orig_calls: number;
  term_minutes: number;
  term_calls: number;
  total_cost: number;
}

export async function getCDRDailyTrend(days = 7): Promise<CDRDailyTrendItem[]> {
  const response = await apiClient.get(`${BASE_URL}/cdrs/daily/`, {
    params: { days },
  });
  return response.data.data || [];
}

/**
 * DID utilization - DIDs with/without traffic in last 30 days
 */
export interface DIDUtilizationData {
  active_count: number;
  stale_count: number;
  utilization_pct: number;
  stale_dids: string[];
}

export async function getDIDUtilization(): Promise<DIDUtilizationData> {
  const response = await apiClient.get(`${BASE_URL}/cdrs/utilization/`);
  return response.data.data || { active_count: 0, stale_count: 0, utilization_pct: 0, stale_dids: [] };
}

/**
 * Stale DIDs - DIDs with no traffic in last N days (uses pre-aggregated summaries)
 */
export interface StaleDIDsData {
  stale_count: number;
  period_days: number;
  stale_dids?: string[];
}

export async function getStaleDIDs(days = 30, includeList = false): Promise<StaleDIDsData> {
  const response = await apiClient.get(`${BASE_URL}/stale/`, {
    params: { days, include_list: includeList },
  });
  return response.data.data || { stale_count: 0, period_days: days };
}

// =============================================================================
// Omni-Search APIs (Global Search)
// =============================================================================

/**
 * DID result from omni-search
 */
export interface OmniSearchDIDResult {
  tn: string;
  tn_formatted: string;
  state: string | null;
  rate_center: string | null;
}

/**
 * Port order result from omni-search
 */
export interface OmniSearchPortOrderResult {
  id: number;
  porting_from: string | null;
  status: string | null;
  did_count: number;
}

/**
 * Omni-search results
 */
export interface OmniSearchResults {
  dids: OmniSearchDIDResult[];
  port_orders: OmniSearchPortOrderResult[];
}

/**
 * Global omni-search across DIDs and port orders
 */
export async function omniSearch(query: string): Promise<OmniSearchResults> {
  const response = await apiClient.get(`${BASE_URL}/omni-search/`, {
    params: { q: query },
  });
  return response.data.data || { dids: [], port_orders: [] };
}
